<template>
  <!-- Guest User Navigation Bar -->
  <GuestNavBar v-if="isGuest && !isLogin" :headerService="headerService" :login="login" />

  <!-- Logged-in User Navigation Bar -->
  <div
    v-else
    class="shrink-0 relative z-[1001] lg:!z-[1000]"
    :class="[isHeaderIniting && 'header-initing', isGuest && 'history-hidden']"
  >
    <PtAIHistoryNav
      v-model="opened"
      :menus="navMenuList as any"
      :historyMenu="navHistory as any"
      :messages="historyPreTitleMessages"
      responsive
      @clickHistory="viewResult"
      @removeHistory="onRemoveHistory"
    >
      <!-- pc端logo -->
      <template #productLogo="{ opened }">
        <div
          v-if="isHeaderIniting"
          class="common-nav-wide-logo w-full py-5 relative min-h-[72px] flex items-center justify-center"
        >
          <div class="bg-transparent w-[196px] h-[32px]"></div>
        </div>
        <a
          v-else-if="headerService.whiteLabel && headerService.customLogoVisible"
          class="common-nav-wide-logo w-full py-5 relative custom-logo min-h-[72px]"
          :href="headerService.logoHref || '/home'"
        >
          <span class="px-0 flex justify-center">
            <img
              v-if="opened"
              :src="isDark ? headerService.customWhiteLogoUrl : headerService.customLogoUrl"
              alt="Custom Logo"
              class="custom-logo-img"
            />
            <img
              v-else
              :src="headerService.customLogoUrl"
              alt="Custom Logo"
              class="w-6 h-6 object-contain"
            />
          </span>
        </a>
        <a v-else href="/home" class="common-nav-wide-logo w-full py-5 relative">
          <span class="px-0 flex" :class="{ 'justify-center': !opened, 'justify-start': opened }">
            <PtIcon v-if="opened" :icon="Logo" class="!w-[100px] !h-[30px]" />
            <PtIcon v-else :icon="MiniLogo" class="!w-6 !h-6" />
          </span>
        </a>
      </template>

      <!-- 移动端logo -->
      <template #logo>
        <a
          v-if="headerService.whiteLabel && headerService.customLogoVisible"
          :href="headerService.logoHref || '/home'"
        >
          <img
            :src="headerService.customLogoUrl"
            alt="Custom Logo"
            class="custom-logo-img mobile"
          />
        </a>
        <a v-else href="/home">
          <PtIcon :icon="Logo" class="!w-[100px] !h-[30px]" />
        </a>
      </template>
      <template #avatar="{ opened }">
        <div
          class="eureka-nav-bar-avatar mt-auto border-solid border-white/20 mx-3 flex flex-row items-center gap-4 lg:border-t lg:flex-col lg:py-4 relative"
        >
          <Incognito
            v-if="incognitoVisible"
            class="w-full"
            :opened="opened"
            :menu="incognitoItem"
            @handleClick="removeAllHistory"
          />
          <div
            v-if="!isLogin && opened && showLoginTip"
            class="hidden absolute bottom-[80px] left-0 lg:block w-[196px] p-3 bg-white text-grey-450 rounded-lg shadow-lg"
          >
            <img
              :src="signUpBg"
              alt="Sign Up Background"
              class="absolute z-1 top-0 left-0 w-full rounded-lg"
            />
            <div class="mb-3 relative z-10 flex justify-between items-center">
              <div class="font-semibold text-base">{{ t('GuestToMaster.subject') }}</div>
              <PtIcon
                :icon="iconClose"
                @click="showLoginTip = false"
                class="!w-6 !h-6 fill-grey-450 cursor-pointer"
              />
            </div>
            {{ t('GuestToMaster.rights') }}
            <div class="absolute -bottom-[9px] left-[15px]}">
              <ArrowDown />
            </div>
          </div>
          <!-- <template v-if="!isInFeiShuClient">
                        <EditionTag :isLogin="isLogin" :open="opened" />
                    </template> -->

          <PtNavUserAvatar :open="opened" :service="headerService" @clickLogin="handleLogin">
            <template #userPackageType>
              <UserPackageType />
            </template>
          </PtNavUserAvatar>
        </div>
      </template>
    </PtAIHistoryNav>
    <!-- <MobileTag :isLogin="isLogin" /> -->

    <!-- Search Task Dialog -->
    <SearchTaskDialog
      :isTaskLoading="isTaskLoading"
      ref="searchTaskDialogRef"
      :tasks="sessionsTaskData"
      @task-click="handleSearchTaskClick"
      @new-task="handleNewSearchTask"
      @search="handleSearchTask"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { PtAIHistoryNav, PtNavUserAvatar, PtIcon } from 'patsnap-biz';
import { type HeaderService, type ListItem, config, Site, SLD } from '@patsnap-utils/biz';
import ArrowDown from './components/icons/ArrowDown.vue';
import Incognito from './components/Incognito.vue';
import SearchTaskDialog from './components/SearchTaskDialog.vue';
import GuestNavBar from './components/GuestNavBar.vue';
// import EditionTag from '@eureka/shared/edition-upgrade/src/components/edition/EditionTag.vue';
// import MobileTag from '@eureka/shared/edition-upgrade/src/components/edition-tag/MobileTag.vue';
import { isUserLogin } from '@eureka/utils/account';
import { INCOGNITO_MENU_ITEM, ROUTE_NAME_WHITELIST } from './constants';
import { useNavBarState } from './composables/use-navbar-store';
import type { HistoryRowData, NAV_MENU_TYPE } from './types';
import { useNavBar } from './composables/use-nav-bar';
import { UserPackageType } from '@eureka/shared/header-menu-3';
import { loginSDK } from '@eureka/shared/login-sdk';
import { useDark } from '@vueuse/core';
import _ from 'lodash';

import signUpBg from './assets/sign-up-bg.png';
import iconClose from '@patsnap-ui/icon/assets/solid/CloseMedium.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@patsnap-ui/icon/assets/solid/AddLarge.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/NavWorkspace.svg';
import '@patsnap-ui/icon/assets/solid/ChartTopipc.svg';
import '@patsnap-ui/icon/assets/solid/ChartRing.svg';
import '@patsnap-ui/icon/assets/solid/FolderMany.svg';
import '@patsnap-ui/icon/assets/solid/InsightsTechnology.svg';
import Logo from './assets/logo.svg';
import MiniLogo from './assets/mini-logo.svg';
// import { clientDeferred } from '@eureka/utils/client';
import type { T_Region } from '@patsnap/uniform-setting/dist/locale/types';
import { useRouter } from 'vue-router';
import { cn, en, jp, tw } from './infrastructure/locales';
import type { IPackageType } from '@eureka/services/src/apis/common.api.types';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import { usePlg } from '@eureka/shared/plg-plan/src/composables/use-plg';

const { t } = useI18n({
  messages: {
    cn,
    en,
    jp,
    tw,
  },
});

const router = useRouter();

const props = defineProps<{
  headerService: HeaderService;
  login?: () => void;
  navModuleType: NAV_MENU_TYPE;
}>();

const isDark = useDark();

const {
  opened,
  currentDetailId,
  routeName,
  shouldSave,
  currentRouteFlag,
  updateNavStatus,
  save,
  getSessionsTaskData,
  sessionsTaskData,
  isTaskLoading,
} = useNavBarState();

const searchTaskDialogRef = ref<InstanceType<typeof SearchTaskDialog> | null>(null);

const {
  historyPreTitleMessages,
  viewResult,
  onRemoveHistory,
  removeAllHistory,
  navMenuList,
  navHistory,
} = useNavBar(props as any, searchTaskDialogRef);

const isLogin = computed(() => {
  return props.headerService.userCenterService.logged;
});
const userPackages = ref<IPackageType[]>([]);

props.headerService.userCenterService.beforeLogoutHandlers.add(async function () {
  if (!['cn'].includes(process.env.VUE_APP_DEPLOY_ENV as T_Region)) {
    await import(/* webpackChunkName: "gis" */ './infrastructure/utils/gis')
      .then(({ loadGis, disableAutoSelect }) => {
        return loadGis().then(disableAutoSelect);
      })
      .catch((e) => {
        console.error('[gis] load gis failed', e);
      });
  }
  const service = props.headerService.userCenterService;
  service.logged = false;
  service.setUserInfo();
  localStorage.removeItem('patsnap-authorizations');
  const { href } = new Site({ sld: SLD.PASSPORT, pathname: 'logout' });
  const guestPage = `${location.origin}/mse/#/home`;
  location.href = `${href}?from=${config.clientName}&redirect_uri=${guestPage}`;
  throw new Error('Logout in NavBar');
});

const { isPersonal } = usePlg();
const buildin = props.headerService.userCenterService.beforeRenderMenuItems;
props.headerService.userCenterService.beforeRenderMenuItems = function (items: ListItem[]) {
  const _items = buildin(items);
  if (isPersonal.value) {
    const nonePackages = ['package', 'settings', 'team'];
    return _items.filter((item) => !nonePackages.includes(item.key as string));
  }
  return _items;
};

const showLoginTip = ref(true);
const isGuest = ref(false);

const isHeaderIniting = ref(!!props.headerService.deferred.promise);

props.headerService.deferred.promise.then(() => {
  isHeaderIniting.value = false;
});

// const userCenterService = computed(() => {
//     console.log(_.get(props.headerService, 'userCenterService.items'), 'userCenterService-111');
//     return _.get(props.headerService, 'userCenterService.items');
// });
// console.log(userCenterService.value, 'userCenterService-222');
const incognitoItem = computed(() => {
  return {
    ...INCOGNITO_MENU_ITEM,
    title: t(`NavBar.${INCOGNITO_MENU_ITEM.type}`),
    tip: t(`NavBar.${INCOGNITO_MENU_ITEM.type}_TIP`),
  };
});

const incognitoVisible = computed(() => {
  return _.get(props.headerService.account, '_info.incognito_flag');
});

// const isInFeiShuClient = ref(false)

// onMounted(() => {
//     clientDeferred.promise.then(res => {
//         isInFeiShuClient.value = res.isFeiShu
//     })
// })

watch(
  () => currentRouteFlag.value,
  () => {
    if (_.includes(ROUTE_NAME_WHITELIST, routeName.value) || !currentDetailId.value) return;
    updateNavStatus(false, false);
  },
  { immediate: true },
);

watch(
  () => opened.value,
  (value: boolean) => {
    if (shouldSave.value) {
      save(value as boolean);
    }
  },
);

function handleLogin() {
  if (props.login) {
    props.login();
    return;
  }
  loginSDK.open().then(() => {
    window.location.reload();
  });
}

function handleSearchTaskClick(task: HistoryRowData) {
  window.location.href = task.link;
  // router.push({ name: 'AiSearchResult', params: { conversation_id: task.session_id }, query: { query: task.title } });
}

function handleSearchTask(query: string) {
  getSessionsTaskData(query);
}
function handleNewSearchTask() {
  // 跳转到新建搜索任务页面
  router.push({ name: 'new-home' });
}

onMounted(async () => {
  isGuest.value = !(await isUserLogin());
  userPackages.value = await CommonApis.getUserPackageType();
});
</script>

<style lang="scss">
// .patsnap-biz-ai-history-item > div:first-of-type {
//   @apply justify-center;
// }

// .patsnap-biz-ai-history-item > div:first-of-type > div {
//   @apply pr-0 border;
// }

.history-hidden {
  .patsnap-biz-ai-history-item:last-child {
    @apply hidden;
  }
  .patsnap-biz-ai-history-nav-collapse > .patsnap-biz-ai-history-item {
    @apply hidden;
  }
}
.patsnap-biz-ai-history-nav .menu-divide-bottom-line::after {
  display: none;
}
.common-nav-wide-logo {
  &::after {
    display: none;
    content: '';
    @apply absolute left-3 right-3 bottom-0 h-px bg-white/20;
  }

  &.custom-logo {
    .custom-logo-img {
      max-height: 48px;
      max-width: 100%;
      height: auto;
      width: auto;
      @apply transition-all duration-200;

      &.mobile {
        max-height: 24px !important;
        width: auto;
        height: 24px !important;
        object-fit: contain;
        @apply transition-all duration-200;
      }
    }
  }
}
.nav-history-sub-item {
  &.active-menu-item,
  &.tippy-content-active {
    span:last-child {
      display: none !important;
    }
  }
}

// 添加移动端特定样式
.patsnap-biz-mobile-nav-bar {
  .custom-logo-img.mobile {
    max-height: 24px !important;
    height: 24px !important;
    width: auto;
    object-fit: contain;
  }
}

.header-initing {
  .patsnap-biz-nav-header,
  .eureka-nav-bar-avatar {
    display: none;
  }
}
</style>
